import React, { useState, useEffect, useCallback } from "react";
import {
  FaSpinner,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaChartBar,
} from "react-icons/fa";
import apiRequest from "../services/api";

export default function TaxSummaryReport() {
  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const currentYear = today.getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);

  const [endDate, setEndDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `ETB ${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getTaxLabel = (taxType) => {
    switch (taxType) {
      case "OUTPUT_VAT":
        return "TAX Collected (Output TAX)";
      case "INPUT_VAT":
        return "TAX Credit (Input TAX)";
      case "WITHHOLDING_RECEIVABLE":
        return "Withholding Tax Receivable";
      case "WITHHOLDING_PAYABLE":
        return "Withholding Tax Payable";
      case "PENSION_PAYABLE":
        return "Pension Contribution";
      case "PAYE_PAYABLE":
        return "Employee Income Tax (PAYE)";
      case "PROFIT_TAX_PAYABLE":
        return "Profit Tax";
      default:
        return taxType;
    }
  };

  const isCreditTax = (taxType) =>
    taxType === "INPUT_VAT" || taxType === "WITHHOLDING_RECEIVABLE";

  const totalPayable = rows.reduce((sum, row) => {
    if (isCreditTax(row.taxType)) return sum;
    return sum + Number(row.due || 0);
  }, 0);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", `${startDate}T00:00:00`);
      if (endDate) params.append("endDate", `${endDate}T23:59:59`);

      const res = await apiRequest(`reports/tax-summary?${params.toString()}`);
      const payload = res?.data || res;

      setRows(Array.isArray(payload) ? payload : payload?.rows || []);
    } catch (err) {
      setError(err?.message || "Failed to load tax summary. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const inputCls =
    "w-full rounded-xl pl-10 pr-4 py-2 text-sm transition focus:outline-none focus:border-emerald-500/50 bg-white border border-slate-200 text-slate-800 dark:bg-slate-900/50 dark:border-white/[0.08] dark:text-slate-200";

  const card =
    "p-5 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-lg";

  return (
    <div className="min-h-full relative overflow-hidden font-sans p-3 md:p-6 bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      {/* Background glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none bg-blue-500/5 blur-[120px] dark:bg-blue-500/10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none bg-emerald-500/5 blur-[150px] dark:bg-emerald-500/10" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-6">
        {/* HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 rounded-2xl gap-4 border shadow-sm backdrop-blur-xl bg-white border-slate-200 dark:bg-white/[0.03] dark:border-white/[0.08] dark:shadow-2xl">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:bg-gradient-to-r dark:from-white dark:via-slate-200 dark:to-slate-400 dark:bg-clip-text dark:text-transparent flex items-center gap-3">
              <FaChartBar className="text-emerald-600 dark:text-emerald-400" />
              Tax Summary
            </h1>
            <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
              Tax obligations & credits for the selected period
            </p>
          </div>
        </div>

        {/* DATE FILTERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl border backdrop-blur-md bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.05]">
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              Start Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              End Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={card}>
              <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                Tracked Categories
              </span>
              <h2 className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                {rows.length} Active Types
              </h2>
            </div>
            <div className={card}>
              <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                Total Tax Outstanding
              </span>
              <h2 className="text-3xl font-bold text-rose-600 dark:text-rose-400 mt-1">
                {formatCurrency(totalPayable)}
              </h2>
            </div>
          </div>
        )}

        {/* LOADING */}
        {loading && (
          <div className="text-center py-12 rounded-2xl border backdrop-blur-md text-slate-500 bg-white border-slate-200 dark:text-slate-400 dark:bg-white/[0.01] dark:border-white/[0.05]">
            <FaSpinner className="animate-spin inline mr-3 text-blue-500 text-lg" />
            Loading tax summary...
          </div>
        )}

        {/* ERROR */}
        {error && (
          <div className="flex items-center gap-3 justify-center text-center py-8 rounded-2xl border text-rose-600 bg-rose-50 border-rose-200 dark:text-rose-400 dark:bg-rose-950/20 dark:border-rose-500/20">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {/* CONTENT */}
        {!loading && !error && (
          <div
            className="w-full overflow-hidden rounded-2xl border shadow-sm backdrop-blur-xl
            bg-white border-slate-200
            dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-2xl"
          >
            {/* TABLE HEADER */}
            <div
              className="p-4 border-b flex justify-between items-center
              bg-slate-50 border-slate-200
              dark:bg-slate-950/60 dark:border-white/[0.08]"
            >
              <h3 className="text-sm font-bold flex items-center gap-2 text-slate-800 dark:text-white">
                <FaChartBar className="text-emerald-600 dark:text-emerald-400 text-base" />
                Tax Summary Report
              </h3>

              <span
                className="px-2 py-0.5 rounded border font-mono text-xs
                bg-white border-slate-200 text-emerald-600
                dark:bg-slate-900/80 dark:border-white/[0.05] dark:text-emerald-400"
              >
                {rows.length} Tax Types
              </span>
            </div>

            <div className="p-6">
              <div
                className="overflow-x-auto rounded-xl border
                border-slate-200 bg-slate-50/50
                dark:border-white/[0.08] dark:bg-slate-950/40"
              >
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr
                      className="font-bold text-[10px] uppercase tracking-wider border-b
                      bg-slate-100/80 text-slate-500 border-slate-200
                      dark:bg-white/[0.02] dark:text-slate-400 dark:border-white/[0.08]"
                    >
                      <th className="p-3 w-12 text-center">#</th>
                      <th className="p-3">Tax Type</th>
                      <th className="p-3 text-right">Assessed Amount</th>
                      <th className="p-3 text-right">Paid / Credit</th>
                      <th className="p-3 text-right">Balance</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y text-xs divide-slate-100 dark:divide-white/[0.04]">
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan="5"
                          className="p-12 text-center text-xs text-slate-500 dark:text-slate-500"
                        >
                          No tax transactions found for the selected period.
                        </td>
                      </tr>
                    )}

                    {rows.map((row, index) => {
                      const creditTax = isCreditTax(row.taxType);

                      return (
                        <tr
                          key={row.taxType || index}
                          className="transition
                            text-slate-600 hover:bg-slate-50
                            dark:text-slate-300 dark:hover:bg-white/[0.02]"
                        >
                          <td className="p-3 text-center font-mono text-slate-400 dark:text-slate-500">
                            {index + 1}
                          </td>

                          <td className="p-3 font-semibold text-slate-800 dark:text-white">
                            {getTaxLabel(row.taxType)}
                          </td>

                          <td className="p-3 font-mono text-right text-slate-600 dark:text-slate-300">
                            {formatCurrency(row.collected)}
                          </td>

                          <td
                            className={`p-3 font-mono text-right ${
                              creditTax
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-slate-500 dark:text-slate-400"
                            }`}
                          >
                            {formatCurrency(row.paid)}
                          </td>

                          <td
                            className={`p-3 font-mono text-right font-bold ${
                              creditTax
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-rose-600 dark:text-rose-400"
                            }`}
                          >
                            {formatCurrency(row.due)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {rows.length > 0 && (
                    <tfoot>
                      <tr
                        className="text-xs font-bold border-t
                        bg-slate-50 border-slate-200
                        dark:bg-white/[0.02] dark:border-white/[0.08]"
                      >
                        <td
                          colSpan="4"
                          className="p-4 text-right uppercase tracking-wider text-[10px] text-slate-500 dark:text-slate-400"
                        >
                          Net Tax Payable
                        </td>

                        <td
                          className="p-4 text-right font-mono text-sm font-extrabold border-l
                          text-rose-600 border-slate-200
                          dark:text-rose-400 dark:border-white/[0.05]"
                        >
                          {formatCurrency(totalPayable)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
